// apps/frontend/src/features/experiential/lib/chairAudioEngine.ts
import { ChairClip, PlaybackMode } from "../types";

type FetchAudio = (url: string) => Promise<ArrayBuffer>;

interface Voice { gain: GainNode; buffer: AudioBuffer; source?: AudioBufferSourceNode; }

interface EngineOptions {
  // Spatialize voices with HRTF panning: clips are spread across an arc
  // around the listener (best with headphones). Used by Spatial Modes.
  spatial?: boolean;
}

export function createChairEngine(clips: ChairClip[], opts: EngineOptions = {}) {
  let ctx: AudioContext | null = null;
  const voices = new Map<string, Voice>();
  let panners = 0;
  let loop = false;
  let started = 0;
  let closed = false;
  // Bumped on every stop/play; a stale token cancels a pending sequence chain.
  let seqToken = 0;

  function ensureCtx(): AudioContext {
    if (!ctx) ctx = new AudioContext();
    return ctx;
  }

  async function load(fetchAudio: FetchAudio) {
    const c = ensureCtx();
    for (let i = 0; i < clips.length; i++) {
      const clip = clips[i];
      const data = await fetchAudio(clip.url);
      const buffer = await c.decodeAudioData(data);
      const gain = c.createGain();
      gain.gain.value = 1;
      if (opts.spatial) {
        gain.connect(makePanner(c, i, clips.length)).connect(c.destination);
      } else {
        gain.connect(c.destination);
      }
      voices.set(clip.id, { gain, buffer });
    }
  }

  // Spread voices across a 270° arc: first and last end up over the
  // shoulders, middle voices in front — the chairs get places in the room.
  function makePanner(c: AudioContext, i: number, n: number): PannerNode {
    const panner = c.createPanner();
    panner.panningModel = "HRTF";
    const theta = n === 1 ? 0 : (i / (n - 1)) * Math.PI * 1.5 - Math.PI * 0.75;
    const x = Math.sin(theta) * 2;
    const z = -Math.cos(theta) * 2;
    if (panner.positionX) {
      panner.positionX.value = x;
      panner.positionZ.value = z;
    } else {
      // Safari fallback
      panner.setPosition(x, 0, z);
    }
    panners++;
    return panner;
  }

  function setGain(id: string, value: number) {
    const v = voices.get(id);
    if (v) v.gain.gain.value = value;
  }

  function setLoop(on: boolean) { loop = on; }

  async function play(mode: PlaybackMode = "together") {
    const c = ensureCtx();
    if (c.state === "suspended") await c.resume();
    stop(); // idempotent re-trigger: stop any prior sources so looping audio can't orphan
    started = 0;
    if (mode === "sequence") {
      playChainFrom(c, 0, ++seqToken);
      return;
    }
    voices.forEach((v) => {
      const source = c.createBufferSource();
      source.buffer = v.buffer;
      source.loop = loop;
      source.connect(v.gain);
      source.start();
      v.source = source;
      started++;
    });
  }

  // Plays clips[i..] one after another in clip order; loop restarts the chain.
  function playChainFrom(c: AudioContext, i: number, token: number) {
    if (token !== seqToken) return;
    if (i >= clips.length) {
      if (loop) playChainFrom(c, 0, token);
      return;
    }
    const v = voices.get(clips[i].id);
    if (!v) throw new Error(`chair engine: no loaded voice for clip ${clips[i].id}`);
    const source = c.createBufferSource();
    source.buffer = v.buffer;
    source.connect(v.gain);
    source.onended = () => { if (token === seqToken) playChainFrom(c, i + 1, token); };
    source.start();
    v.source = source;
    started++;
  }

  function stop() {
    seqToken++;
    voices.forEach((v) => {
      try { v.source?.stop(); } catch { /* already stopped */ }
      v.source = undefined;
    });
  }

  async function close() {
    stop();
    if (ctx) { await ctx.close(); closed = true; }
  }

  return {
    load, play, stop, setGain, setLoop, close,
    // test-only inspectors
    _debugGainCount: () => voices.size,
    _debugGain: (id: string) => voices.get(id)?.gain.gain.value,
    _debugStartedCount: () => started,
    _debugClosed: () => closed,
    _debugPannerCount: () => panners,
  };
}
